import { Stack, Text, ActionList, ActionListItem } from "@certn/cascadia";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { Schemas } from "apps/client-portal/src/types";
import { useTranslation } from "react-i18next";
import { CreateEditAdjudicationFlow } from "../components/CreateEditAdjudicationFlow";
import { SubCategory } from "../types/canadian-criminal-record-types";
import { getCheckTypeName } from "../utils";

interface AdjudicationChecksProps {
  adjudicationData?: Schemas["AdjudicationRubricDetails"];
  isLoading: boolean;
  selectedCheckType: string | null;
  onNavigationChange: (checkType: string | null) => void;
  selectedSubCategory: SubCategory | null;
  onSubCategorySelect: (subCategory: SubCategory) => void;
}

export function AdjudicationChecks({
  adjudicationData,
  isLoading,
  selectedCheckType,
  onNavigationChange,
  selectedSubCategory,
  onSubCategorySelect,
}: AdjudicationChecksProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Stack gap="space-4">
        <ActionList>
          {Array.from({ length: 4 }).map((_, index) => (
            <ActionListItem
              key={`skeleton-${index}`}
              title={
                <Stack gap="space-1">
                  <Skeleton height="20px" width="200px" />
                  <Skeleton height="14px" width="120px" />
                </Stack>
              }
              trailing={<Skeleton height="24px" width="80px" />}
            />
          ))}
        </ActionList>
      </Stack>
    );
  }

  if (!adjudicationData?.rulesets?.length) {
    return (
      <Stack gap="space-4">
        <Text style="global/body-secondary">{t("No checks configured")}</Text>
      </Stack>
    );
  }

  // Map the ruleset to extract check types with human-readable names
  const checkTypes = adjudicationData.rulesets.map((rule: unknown) => ({
    id: (rule as { check_type: string }).check_type,
    name: getCheckTypeName((rule as { check_type: string }).check_type, t),
  }));

  return (
    <Stack gap="space-4">
      {!selectedCheckType && (
        <Text style="global/heading-large">{t("Edit rules")}</Text>
      )}
      <CreateEditAdjudicationFlow
        selectedCheckType={selectedCheckType}
        onNavigationChange={onNavigationChange}
        selectedSubCategory={selectedSubCategory}
        onSubCategorySelect={onSubCategorySelect}
        checkTypes={checkTypes}
      />
    </Stack>
  );
}
